import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, 
  FileDown, 
  Filter,
  Calendar,
  Building2,
  User,
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  UserCheck,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import AccionesGestion from './AccionesGestion';
import EditarGestion from './EditarGestion';
import DetalleGestion from './DetalleGestion';
import { useAuth } from '../context/AuthContext';
import { htmlToPlainText } from '../utils/htmlToPlainText';
import { parseLocalDate, formatLocalDate } from '../utils/dateHelpers';
import './VistaGeneralGestiones.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VistaGeneralGestiones = ({ onViewDetail, onEditGestion }) => {
  const { user } = useAuth();
  const [gestiones, setGestiones] = useState([]);
  const [gestionesOriginales, setGestionesOriginales] = useState([]);
  const [instituciones, setInstituciones] = useState([]);
  const [asesoresCNC, setAsesoresCNC] = useState([]);
  const [loading, setLoading] = useState(false);
  const [estadisticas, setEstadisticas] = useState(null);
  const [view, setView] = useState('list');
  const [selectedGestion, setSelectedGestion] = useState(null);
  const [showFilters, setShowFilters] = useState(true); // Estado para ocultar/mostrar filtros
  
  // Preseleccionar filtro de asesor para usuarios con rol 'analyst'
  const getInitialFiltros = () => {
    const baseFiltros = {
      q: '',
      tipo_gestion: '',
      estado: '',
      venc_cnc_desde: '',
      venc_cnc_hasta: '',
      venc_ip_desde: '',
      venc_ip_hasta: '',
      institucion: '',
      asesor_cnc: '',
      es_urgente: ''
    };
    
    // Si el usuario es analista, preseleccionar su nombre
    if (user?.role === 'analyst' && user?.full_name) {
      baseFiltros.asesor_cnc = user.full_name;
    }
    
    return baseFiltros;
  };
  
  const [filtros, setFiltros] = useState(getInitialFiltros());

  // Actualizar filtros cuando el usuario cambie
  useEffect(() => {
    if (user?.role === 'analyst' && user?.full_name && !filtros.asesor_cnc) {
      setFiltros(prev => ({ ...prev, asesor_cnc: user.full_name }));
    }
  }, [user]);

  useEffect(() => {
    fetchGestiones();
    fetchInstituciones();
    fetchAsesoresCNC();
  }, []);

  const fetchGestiones = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gestiones`);
      const data = response.data || [];
      setGestiones(data);
      setGestionesOriginales(data);
      // Calcular estadísticas locales con la lógica correcta
      setEstadisticas(calcularEstadisticasLocales(data));
    } catch (error) {
      console.error('Error al cargar gestiones:', error);
      toast.error('Error al cargar las gestiones');
    } finally {
      setLoading(false);
    }
  };

  const fetchInstituciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogs/institutions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activas = (response.data || []).filter(inst => inst.is_active);
      setInstituciones(activas);
    } catch (error) {
      console.error('Error al cargar instituciones:', error);
    }
  };

  const fetchAsesoresCNC = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/catalogos/asesores-cnc?activo=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAsesoresCNC(response.data || []);
    } catch (error) {
      console.error('Error al cargar asesores CNC:', error);
    }
  };

  const handleBuscar = async () => {
    setLoading(true);
    try {
      // Aplicar filtros localmente desde datos originales
      let resultado = [...gestionesOriginales];
      
      // Filtro de texto general
      if (filtros.q) {
        const q = filtros.q.toLowerCase();
        resultado = resultado.filter(g => 
          g.nomenclatura?.toLowerCase().includes(q) ||
          g.descripcion?.toLowerCase().includes(q) ||
          g.institucion?.toLowerCase().includes(q) ||
          g.nombre_asesor_cnc?.toLowerCase().includes(q)
        );
      }
      
      // Filtro por tipo de gestión
      if (filtros.tipo_gestion) {
        resultado = resultado.filter(g => g.tipo_gestion === filtros.tipo_gestion);
      }
      
      // Filtro por estado
      if (filtros.estado) {
        resultado = resultado.filter(g => g.estado === filtros.estado);
      }
      
      // Filtro por institución
      if (filtros.institucion) {
        resultado = resultado.filter(g => g.institucion === filtros.institucion);
      }
      
      // Filtro por asesor CNC
      if (filtros.asesor_cnc) {
        resultado = resultado.filter(g => g.nombre_asesor_cnc === filtros.asesor_cnc);
      }
      
      // Filtro por vencimiento CNC desde
      if (filtros.venc_cnc_desde) {
        resultado = resultado.filter(g => 
          g.fecha_vencimiento && new Date(g.fecha_vencimiento) >= new Date(filtros.venc_cnc_desde)
        );
      }
      
      // Filtro por vencimiento CNC hasta
      if (filtros.venc_cnc_hasta) {
        resultado = resultado.filter(g => 
          g.fecha_vencimiento && new Date(g.fecha_vencimiento) <= new Date(filtros.venc_cnc_hasta)
        );
      }
      
      // Filtro por vencimiento IP desde
      if (filtros.venc_ip_desde) {
        resultado = resultado.filter(g => 
          g.fecha_vencimiento_probidad && new Date(g.fecha_vencimiento_probidad) >= new Date(filtros.venc_ip_desde)
        );
      }
      
      // Filtro por vencimiento IP hasta
      if (filtros.venc_ip_hasta) {
        resultado = resultado.filter(g => 
          g.fecha_vencimiento_probidad && new Date(g.fecha_vencimiento_probidad) <= new Date(filtros.venc_ip_hasta)
        );
      }
      
      // Filtro por urgente
      if (filtros.es_urgente === 'si') {
        resultado = resultado.filter(g => g.es_urgente === true);
      } else if (filtros.es_urgente === 'no') {
        resultado = resultado.filter(g => !g.es_urgente);
      }
      
      setGestiones(resultado);
      toast.success(`${resultado.length} gestiones encontradas`);
    } catch (error) {
      console.error('Error en búsqueda:', error);
      toast.error('Error al realizar la búsqueda');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = (gestion) => {
    if (onViewDetail) {
      onViewDetail(gestion);
    } else {
      setSelectedGestion(gestion);
      setView('detail');
    }
  };

  const handleEditGestion = (gestion) => {
    if (onEditGestion) {
      onEditGestion(gestion);
    } else {
      setSelectedGestion(gestion);
      setView('edit');
    }
  };

  // Vista de detalle
  if (view === 'detail' && selectedGestion) {
    return (
      <DetalleGestion 
        gestionId={selectedGestion.id} 
        onBack={() => {
          setSelectedGestion(null);
          setView('list');
          fetchGestiones();
        }} 
      />
    );
  }

  // Vista de edición
  if (view === 'edit' && selectedGestion) {
    return (
      <EditarGestion 
        gestion={selectedGestion}
        onSuccess={() => {
          setSelectedGestion(null);
          setView('list');
          fetchGestiones();
          toast.success('Gestión actualizada correctamente');
        }}
        onCancel={() => {
          setSelectedGestion(null);
          setView('list');
        }}
      />
    );
  }

  const handleExportarExcel = () => {
    if (gestiones.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const data = gestiones.map(g => ({
      'Nomenclatura': g.nomenclatura,
      'Módulo': getModuloSigla(g.tipo_gestion),
      'Tipo Gestión': g.tipo_gestion,
      'Fecha Asignación CNC': formatLocalDate(g.fecha_asignacion),
      'Vencimiento CNC': formatLocalDate(g.fecha_vencimiento),
      'Institución': g.institucion,
      'Asesor CNC': g.nombre_asesor_cnc,
      'Asignado Probidad': g.asesor_probidad_nombre ? 'Sí' : 'No',
      'Asesor Probidad': g.asesor_probidad_nombre || 'N/A',
      'Fecha Asignación Probidad': formatLocalDate(g.fecha_asignacion_probidad),
      'Vencimiento Probidad': formatLocalDate(g.fecha_vencimiento_probidad),
      'Calificación': g.calificacion_instancia ? `${g.calificacion_instancia.puntuacion}/5` : 'N/A',
      'Estado': g.estado,
      'Enlaces Referenciales': (() => {
        // Consolidar todos los enlaces del caso
        const enlaces = [];
        // Enlaces del campo específico (para monitoreo de medios)
        if (g.campos_especificos?.enlaces?.length > 0) {
          enlaces.push(...g.campos_especificos.enlaces.filter(Boolean));
        } else if (g.campos_especificos?.link) {
          enlaces.push(g.campos_especificos.link);
        }
        // Enlace individual si existe
        if (g.enlace_referencial) {
          enlaces.push(g.enlace_referencial);
        }
        // Enlaces adicionales si existen
        if (g.enlaces_referenciales?.length > 0) {
          enlaces.push(...g.enlaces_referenciales.filter(Boolean));
        }
        // Eliminar duplicados y retornar
        const enlacesUnicos = [...new Set(enlaces)];
        return enlacesUnicos.length > 0 ? enlacesUnicos.join('\n') : 'N/A';
      })(),
      'Descripción': htmlToPlainText(g.descripcion),
      'Creado por': g.createdBy,
      'Fecha Creación': formatLocalDate(g.createdAt)
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Gestiones');

    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Gestiones_ALERTA360_${fecha}.xlsx`);
    
    toast.success('Excel exportado correctamente');
  };

  const getModuloColor = (tipo) => {
    const colores = {
      // Nuevos nombres
      'Monitoreo': '#2196F3',
      'Adquisiciones públicas': '#F44336',
      'Contratación Personal': '#4CAF50',
      'Dirección Ejecutiva': '#FF9800',
      'Casos previos': '#9C27B0',
      // Nombres legacy para compatibilidad
      'Monitoreo de Medios': '#2196F3',
      'Banderas Rojas': '#F44336',
      'Requerimientos de Instancias de Probidad': '#4CAF50',
      'Contratación Personal': '#4CAF50',
      'Gestión de Información': '#FF9800',
      'Seguimiento': '#9C27B0'
    };
    return colores[tipo] || '#757575';
  };

  const getModuloSigla = (tipo) => {
    const siglas = {
      // Nuevas nomenclaturas
      'Monitoreo': 'MM',
      'Adquisiciones públicas': 'AP',
      'Contratación Personal': 'CDP',
      'Dirección Ejecutiva': 'DE',
      'Casos previos': 'CP',
      // Nomenclaturas legacy para compatibilidad
      'Monitoreo de Medios': 'MM',
      'Banderas Rojas': 'BR',
      'Requerimientos de Instancias de Probidad': 'CDP',
      'Gestión de Información': 'GI',
      'Seguimiento': 'SEG'
    };
    return siglas[tipo] || '???';
  };

  const getEstadoIcon = (estado) => {
    const icons = {
      'Registrado': <Clock size={16} />,
      'En proceso de análisis': <AlertCircle size={16} />,
      'Enviado / Compartido': <AlertCircle size={16} />,
      'Completada / Archivada': <CheckCircle size={16} />,
      'Completada': <CheckCircle size={16} />
    };
    return icons[estado] || <Clock size={16} />;
  };

  // Función para verificar si está vencido CNC (NO incluye las derivadas a probidad)
  // Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
  const esVencidoCNC = (fecha, estado, asesorProbidadId, asesorProbidadNombre) => {
    if (!fecha) return false;
    if (estado === 'Completada / Archivada' || estado === 'Completada') return false;
    // Si ya fue derivada a Probidad, NO se considera vencido para CNC
    if (asesorProbidadId || asesorProbidadNombre) return false;
    const fechaVenc = parseLocalDate(fecha);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return fechaVenc && hoy > fechaVenc;
  };

  // Función para verificar si fue atendida en tiempo (derivada aunque pasó fecha)
  const esAtendidoEnTiempo = (fecha, estado, asesorProbidadId, asesorProbidadNombre) => {
    if (!fecha) return false;
    if (estado === 'Completada / Archivada' || estado === 'Completada') return false;
    const yaDerivada = asesorProbidadId || asesorProbidadNombre;
    const fechaVenc = parseLocalDate(fecha);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return yaDerivada && fechaVenc && hoy > fechaVenc;
  };

  // Función para verificar si está vencido Probidad
  // Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
  const esVencidoProbidad = (fecha, estado) => {
    if (!fecha) return false;
    if (estado === 'Completada / Archivada' || estado === 'Completada') return false;
    const fechaVenc = parseLocalDate(fecha);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return fechaVenc && hoy > fechaVenc;
  };

  // Calcular estadísticas locales considerando la lógica correcta
  // Una gestión está VENCIDA a partir del DÍA SIGUIENTE a la fecha de vencimiento
  const calcularEstadisticasLocales = (data) => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return {
      total: data.length,
      asignadas_probidad: data.filter(g => g.asesor_probidad_id || g.asesor_probidad_nombre).length,
      pendientes_derivacion: data.filter(g => !g.asesor_probidad_id && !g.asesor_probidad_nombre && g.estado !== 'Completada / Archivada').length,
      completadas: data.filter(g => g.estado === 'Completada / Archivada' || g.estado === 'Completada').length,
      // Vencidas CNC: solo las que NO han sido derivadas a probidad
      vencidas_cnc: data.filter(g => {
        if (!g.fecha_vencimiento) return false;
        if (g.estado === 'Completada / Archivada' || g.estado === 'Completada') return false;
        if (g.asesor_probidad_id || g.asesor_probidad_nombre) return false;
        const fechaVenc = parseLocalDate(g.fecha_vencimiento);
        return fechaVenc && hoy > fechaVenc;
      }).length,
      // Vencidas Probidad: SOLO si tiene fecha_vencimiento_probidad y está vencida
      // NO usar fallback de fecha_vencimiento
      vencidas_probidad: data.filter(g => {
        if (g.estado === 'Completada / Archivada' || g.estado === 'Completada') return false;
        if (!g.fecha_vencimiento_probidad) return false;
        const fechaVenc = parseLocalDate(g.fecha_vencimiento_probidad);
        return fechaVenc && hoy > fechaVenc;
      }).length,
      derivadas_probidad: data.filter(g => g.asesor_probidad_id || g.asesor_probidad_nombre).length,
      improcedentes: data.filter(g => g.resultado_final === 'Improcedente' || g.es_procedente === false).length
    };
  };

  return (
    <div className="vista-general-container">
      {/* Header con Estadísticas */}
      <div className="vista-header">
        <div className="vista-title-section">
          <h1>Vista General de Gestiones</h1>
          <p>Gestión transversal de todos los módulos del sistema ALERTA 360</p>
        </div>

        {estadisticas && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{estadisticas.total}</div>
              <div className="stat-label">Total Gestiones</div>
            </div>
            <div className="stat-card stat-info">
              <div className="stat-value">{estadisticas.derivadas_probidad || 0}</div>
              <div className="stat-label">Derivadas Probidad</div>
            </div>
            <div className="stat-card stat-warning">
              <div className="stat-value">{estadisticas.pendientes_derivacion || 0}</div>
              <div className="stat-label">Pend. Derivación</div>
            </div>
            <div className="stat-card stat-success">
              <div className="stat-value">{estadisticas.completadas || 0}</div>
              <div className="stat-label">Completadas</div>
            </div>
            <div className="stat-card stat-danger">
              <div className="stat-value">{estadisticas.vencidas_cnc || 0}</div>
              <div className="stat-label">Vencidas CNC</div>
            </div>
            <div className="stat-card stat-danger" style={{ borderLeftColor: '#FF5722' }}>
              <div className="stat-value">{estadisticas.vencidas_probidad || 0}</div>
              <div className="stat-label">Vencidas Probidad</div>
            </div>
          </div>
        )}
      </div>

      {/* Filtros Avanzados */}
      <div className="panel filtros-avanzados">
        <div className="panel-header" style={{ cursor: 'pointer' }} onClick={() => setShowFilters(!showFilters)}>
          <h2>
            <Filter size={20} />
            Filtros de Búsqueda
          </h2>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }}>
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showFilters ? 'Ocultar' : 'Mostrar'}
          </button>
        </div>
        {showFilters && (
        <div className="filtros-grid">
          <div className="form-field">
            <label>
              <Search size={16} />
              Búsqueda General
            </label>
            <input
              type="text"
              placeholder="Nomenclatura, descripción, institución..."
              value={filtros.q}
              onChange={(e) => setFiltros({...filtros, q: e.target.value})}
            />
          </div>

          <div className="form-field">
            <label>Tipo de Gestión</label>
            <select value={filtros.tipo_gestion} onChange={(e) => setFiltros({...filtros, tipo_gestion: e.target.value})}>
              <option value="">Todos los módulos</option>
              <option value="Monitoreo">Monitoreo (MM)</option>
              <option value="Adquisiciones públicas">Adquisiciones Públicas (AP)</option>
              <option value="Contratación Personal">Contratación Personal</option>
              <option value="Dirección Ejecutiva">Dirección Ejecutiva (DE)</option>
              <option value="Casos previos">Casos Previos (CP)</option>
            </select>
          </div>

          <div className="form-field">
            <label>Estado</label>
            <select value={filtros.estado} onChange={(e) => setFiltros({...filtros, estado: e.target.value})}>
              <option value="">Todos los estados</option>
              <option value="Registrado">Registrado</option>
              <option value="En proceso de análisis">En proceso de análisis</option>
              <option value="Enviado / Compartido">Enviado / Compartido</option>
              <option value="En Revisión">En Revisión</option>
              <option value="En espera de otra gestión">En espera de otra gestión</option>
              <option value="Completada / Archivada">Completada / Archivada</option>
            </select>
          </div>

          <div className="form-field">
            <label>
              <Calendar size={16} />
              Venc. CNC Desde
            </label>
            <input
              type="date"
              value={filtros.venc_cnc_desde}
              onChange={(e) => setFiltros({...filtros, venc_cnc_desde: e.target.value})}
            />
          </div>

          <div className="form-field">
            <label>
              <Calendar size={16} />
              Venc. CNC Hasta
            </label>
            <input
              type="date"
              value={filtros.venc_cnc_hasta}
              onChange={(e) => setFiltros({...filtros, venc_cnc_hasta: e.target.value})}
            />
          </div>

          <div className="form-field">
            <label>
              <Calendar size={16} />
              Venc. IP Desde
            </label>
            <input
              type="date"
              value={filtros.venc_ip_desde}
              onChange={(e) => setFiltros({...filtros, venc_ip_desde: e.target.value})}
            />
          </div>

          <div className="form-field">
            <label>
              <Calendar size={16} />
              Venc. IP Hasta
            </label>
            <input
              type="date"
              value={filtros.venc_ip_hasta}
              onChange={(e) => setFiltros({...filtros, venc_ip_hasta: e.target.value})}
            />
          </div>

          <div className="form-field">
            <label>
              <Building2 size={16} />
              Institución
            </label>
            <select
              value={filtros.institucion}
              onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}
            >
              <option value="">Todas las instituciones</option>
              {instituciones.map((inst) => (
                <option key={inst.id} value={inst.value}>
                  {inst.value}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>
              <User size={16} />
              Asesor CNC
              {user?.role === 'analyst' && (
                <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.5rem' }}>(fijo)</span>
              )}
            </label>
            <select
              value={filtros.asesor_cnc}
              onChange={(e) => setFiltros({...filtros, asesor_cnc: e.target.value})}
              disabled={user?.role === 'analyst'}
              style={user?.role === 'analyst' ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
            >
              <option value="">Todos los asesores</option>
              {asesoresCNC.map((asesor) => (
                <option key={asesor.id} value={asesor.nombre_completo}>
                  {asesor.nombre_completo}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label>
              <AlertTriangle size={16} />
              Casos Urgentes
            </label>
            <select
              value={filtros.es_urgente}
              onChange={(e) => setFiltros({...filtros, es_urgente: e.target.value})}
            >
              <option value="">Todos</option>
              <option value="si">🔴 Solo Urgentes</option>
              <option value="no">No Urgentes</option>
            </select>
          </div>

          <div className="form-field" style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
            <button className="btn btn-primary" onClick={handleBuscar} style={{ flex: 1 }}>
              <Search size={16} />
              Buscar
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                // Si es analista, mantener su nombre en el filtro
                const nuevosFiltros = {
                  q: '', tipo_gestion: '', estado: '', 
                  venc_cnc_desde: '', venc_cnc_hasta: '',
                  venc_ip_desde: '', venc_ip_hasta: '',
                  institucion: '', es_urgente: '',
                  asesor_cnc: user?.role === 'analyst' && user?.full_name ? user.full_name : ''
                };
                setFiltros(nuevosFiltros);
                fetchGestiones();
              }}
            >
              Limpiar
            </button>
          </div>
        </div>
        )}
      </div>

      {/* Tabla de Gestiones */}
      <div className="panel">
        <div className="panel-header">
          <h2>Gestiones Registradas ({gestiones.length})</h2>
          <button className="btn btn-secondary" onClick={handleExportarExcel}>
            <FileDown size={16} />
            Exportar Excel
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <div className="loader"></div>
            <p style={{ marginTop: '1rem', color: 'var(--color-text-muted)' }}>Cargando gestiones...</p>
          </div>
        ) : gestiones.length === 0 ? (
          <div className="empty-state">
            <Search size={48} style={{ opacity: 0.3 }} />
            <p>No hay gestiones que coincidan con los filtros</p>
          </div>
        ) : (
          <div className="gestiones-table-wrapper">
            <table className="gestiones-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '70px' }}>Módulo</th>
                  <th style={{ minWidth: '100px' }}>Nomenclatura</th>
                  <th style={{ minWidth: '150px' }}>Institución</th>
                  <th style={{ minWidth: '140px' }}>Asesor CNC</th>
                  <th style={{ minWidth: '90px' }}>Venc. CNC</th>
                  <th style={{ minWidth: '70px' }}>Probidad</th>
                  <th style={{ minWidth: '100px' }}>Fecha Derivación</th>
                  <th style={{ minWidth: '140px' }}>Asesor Probidad</th>
                  <th style={{ minWidth: '100px' }}>Venc. Probidad</th>
                  <th style={{ minWidth: '100px' }}>Fecha Completado</th>
                  <th style={{ minWidth: '80px' }}>Calificación</th>
                  <th style={{ minWidth: '120px' }}>Estado</th>
                  <th style={{ minWidth: '80px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {gestiones.map((gestion) => {
                  const vencidoCNC = esVencidoCNC(gestion.fecha_vencimiento, gestion.estado, gestion.asesor_probidad_id, gestion.asesor_probidad_nombre);
                  const atendidoTiempo = esAtendidoEnTiempo(gestion.fecha_vencimiento, gestion.estado, gestion.asesor_probidad_id, gestion.asesor_probidad_nombre);
                  const vencidoProbidad = esVencidoProbidad(gestion.fecha_vencimiento_probidad, gestion.estado);
                  
                  return (
                  <tr key={gestion.id}>
                    <td>
                      <span 
                        className="modulo-badge" 
                        style={{ backgroundColor: getModuloColor(gestion.tipo_gestion) }}
                      >
                        {getModuloSigla(gestion.tipo_gestion)}
                      </span>
                    </td>
                    <td>
                      <strong style={{ color: getModuloColor(gestion.tipo_gestion) }}>
                        {gestion.nomenclatura}
                      </strong>
                    </td>
                    <td>{gestion.institucion}</td>
                    <td>{gestion.nombre_asesor_cnc}</td>
                    <td>
                      {gestion.fecha_vencimiento ? (
                        <span style={{ 
                          color: vencidoCNC ? '#D32F2F' : atendidoTiempo ? '#2E7D32' : 'inherit',
                          fontWeight: (vencidoCNC || atendidoTiempo) ? 600 : 'normal'
                        }}>
                          {vencidoCNC && <AlertTriangle size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                          {atendidoTiempo && <span style={{ marginRight: '4px', color: '#2E7D32' }}>✓</span>}
                          {formatLocalDate(gestion.fecha_vencimiento)}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                      )}
                    </td>
                    <td>
                      {gestion.asesor_probidad_nombre ? (
                        <span className="badge badge-success" style={{ fontSize: '0.7rem' }}>
                          <UserCheck size={12} style={{ marginRight: '2px' }} />
                          Sí
                        </span>
                      ) : (
                        <span className="badge badge-secondary" style={{ fontSize: '0.7rem' }}>No</span>
                      )}
                    </td>
                    <td>
                      {gestion.fecha_asignacion_probidad ? (
                        <span>{formatLocalDate(gestion.fecha_asignacion_probidad)}</span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                      )}
                    </td>
                    <td>{gestion.asesor_probidad_nombre || <span style={{ color: 'var(--color-text-muted)' }}>-</span>}</td>
                    <td>
                      {gestion.fecha_vencimiento_probidad ? (
                        <span style={{ 
                          color: vencidoProbidad ? '#D32F2F' : 'inherit',
                          fontWeight: vencidoProbidad ? 600 : 'normal'
                        }}>
                          {vencidoProbidad && <AlertTriangle size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                          {formatLocalDate(gestion.fecha_vencimiento_probidad)}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                      )}
                    </td>
                    <td>
                      {(gestion.estado === 'Completada / Archivada' || gestion.estado === 'Completada') && gestion.updatedAt ? (
                        <span style={{ color: '#2E7D32' }}>
                          {formatLocalDate(gestion.updatedAt)}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                      )}
                    </td>
                    <td>
                      {gestion.calificacion_instancia ? (
                        <span style={{ color: '#FFB800', fontWeight: 600, fontSize: '0.9rem' }} title={`Calificación: ${gestion.calificacion_instancia.puntuacion}/5`}>
                          {'★'.repeat(gestion.calificacion_instancia.puntuacion)}
                          {'☆'.repeat(5 - gestion.calificacion_instancia.puntuacion)}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-estado ${gestion.estado === 'Completada / Archivada' || gestion.estado === 'Completada' ? 'badge-success' : gestion.estado?.includes('proceso') ? 'badge-info' : 'badge-warning'}`}>
                        {getEstadoIcon(gestion.estado)}
                        {gestion.estado}
                      </span>
                    </td>
                    <td>
                      <AccionesGestion
                        gestion={gestion}
                        onView={handleViewDetail}
                        onEdit={handleEditGestion}
                        compact={true}
                      />
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default VistaGeneralGestiones;
